import { useState, useEffect, useRef } from 'react';

/**
 * Known admin capabilities (hardcoded for role creation)
 * This list should match the capabilities registered in the backend.
 * If capabilities change, this list should be updated.
 */
const KNOWN_CAPABILITIES = [
  'admin:audit:read',
  'admin:integrations:read',
  'admin:integrations:manage',
  'admin:api-keys:read',
  'admin:api-keys:manage',
  'admin:owner-dashboard:read',
  'admin:owner-dashboard:manage',
  'admin:intent:create',
  'admin:rbac:read',
  'admin:rbac:manage',
  'admin:users:read',
  'admin:users:create',
  'admin:users:update',
  'admin:users:activate',
  'admin:users:delete',
  'admin:roles:read',
  'admin:roles:create',
  'admin:roles:update',
  'admin:roles:delete',
  'admin:roles:manage-capabilities',
  'admin:rbac:assign',
  'admin:rbac:remove',
];

/**
 * RoleCreateModal
 * 
 * Modal for creating a new role with name, description, capabilities, and reason.
 */
export default function RoleCreateModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  error = null,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [capabilitySearch, setCapabilitySearch] = useState('');
  const [reason, setReason] = useState('');
  const nameInputRef = useRef(null);
  const modalRef = useRef(null);

  // Filter capabilities by search term
  const filteredCapabilities = KNOWN_CAPABILITIES.filter(cap =>
    cap.toLowerCase().includes(capabilitySearch.toLowerCase())
  );

  // Focus name field when modal opens
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setSelectedCapabilities([]);
      setCapabilitySearch('');
      setReason('');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleToggleCapability = (capability) => {
    setSelectedCapabilities(prev => {
      if (prev.includes(capability)) {
        return prev.filter(c => c !== capability);
      } else {
        return [...prev, capability];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!name.trim() || !reason.trim()) {
      return;
    }
    
    if (onConfirm && typeof onConfirm === 'function') {
      onConfirm({
        name: name.trim(),
        description: description.trim() || null,
        capabilities: selectedCapabilities,
        reason: reason.trim(),
      });
    }
  };

  // Role name validation (lowercase alphanumeric + hyphens/underscores)
  const nameRegex = /^[a-z0-9_-]*$/;
  const isValidName = name.trim() && nameRegex.test(name.trim());
  const canSubmit = isValidName && reason.trim().length > 0 && !loading;

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="presentation"
      aria-hidden={!open}
    >
      <div
        ref={modalRef}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-create-title"
      >
        <div style={styles.header}>
          <h2 id="role-create-title" style={styles.title}>Create Role</h2>
          {!loading && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close dialog"
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.content}>
            <p style={styles.description}>
              Create a new role with assigned capabilities. A reason is required for audit logging.
            </p>

            <div style={styles.formGroup}>
              <label htmlFor="role-create-name" style={styles.label}>
                Role Name <span style={styles.required}>*</span>
              </label>
              <input
                id="role-create-name"
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                placeholder="operator"
                style={{
                  ...styles.input,
                  ...(name.trim() && !isValidName && styles.inputError),
                }}
                disabled={loading}
                aria-required="true"
                aria-invalid={name.trim() && !isValidName}
              />
              {name.trim() && !isValidName && (
                <p style={styles.errorText}>Role name must be lowercase alphanumeric with hyphens or underscores only</p>
              )}
              <p style={styles.helpText}>Lowercase letters, numbers, hyphens, and underscores only.</p>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="role-create-description" style={styles.label}>
                Description
              </label>
              <input
                id="role-create-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional role description"
                style={styles.input}
                disabled={loading}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Capabilities
              </label>
              <input
                type="text"
                value={capabilitySearch}
                onChange={(e) => setCapabilitySearch(e.target.value)}
                placeholder="Search capabilities..."
                style={styles.input}
                disabled={loading}
              />
              <div style={styles.capabilityList}>
                {filteredCapabilities.map(capability => (
                  <label key={capability} style={styles.capabilityItem}>
                    <input
                      type="checkbox"
                      checked={selectedCapabilities.includes(capability)}
                      onChange={() => handleToggleCapability(capability)}
                      disabled={loading}
                      style={styles.checkbox}
                    />
                    <code style={styles.capabilityCode}>{capability}</code>
                  </label>
                ))}
              </div>
              {selectedCapabilities.length > 0 && (
                <p style={styles.helpText}>
                  {selectedCapabilities.length} capability{selectedCapabilities.length !== 1 ? 'ies' : ''} selected
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="role-create-reason" style={styles.label}>
                Reason <span style={styles.required}>*</span>
              </label>
              <textarea
                id="role-create-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter a reason for creating this role..."
                style={{
                  ...styles.textarea,
                  ...(!reason.trim() && styles.textareaError),
                }}
                rows={4}
                disabled={loading}
                aria-required="true"
              />
              {!reason.trim() && (
                <p style={styles.errorText}>Reason is required</p>
              )}
              <p style={styles.helpText}>
                This reason will be recorded in the audit log.
              </p>
            </div>

            {error && (
              <div style={styles.error} role="alert" aria-live="polite">
                <p>{error}</p>
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <button
              onClick={onClose}
              disabled={loading}
              style={styles.cancelButton}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || loading}
              style={{
                ...styles.submitButton,
                ...((!canSubmit || loading) && styles.submitButtonDisabled),
              }}
              aria-disabled={!canSubmit || loading}
            >
              {loading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent modal from scrolling, only content scrolls
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0, // Header doesn't shrink
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#111827',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
    borderRadius: '0.25rem',
    transition: 'background-color 0.2s',
  },
  content: {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0, // Allow flex item to shrink below content size
  },
  description: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: '#111827',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    backgroundColor: '#fff',
    color: '#111827',
    lineHeight: '1.5',
    transition: 'border-color 0.2s',
  },
  textareaError: {
    borderColor: '#ef4444',
  },
  errorText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#ef4444',
  },
  helpText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  capabilityList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    padding: '0.5rem',
    marginTop: '0.5rem',
    backgroundColor: '#f9fafb',
  },
  capabilityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    cursor: 'pointer',
    borderRadius: '0.25rem',
    transition: 'background-color 0.2s',
  },
  checkbox: {
    cursor: 'pointer',
  },
  capabilityCode: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: '#111827',
  },
  error: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    border: '1px solid #ef4444',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1.5rem',
    borderTop: '1px solid #e5e7eb',
    flexShrink: 0, // Footer doesn't shrink, stays visible
    backgroundColor: '#fff', // Ensure footer has background for sticky effect
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButton: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#10b981',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#9ca3af',
  },
};

